/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { GlobalFactStore } from '../rules/facts/store';
import { getPrebidUserId } from '../util';
import { resolvePath } from './path';

import type {
  AdserverKvSource,
  ArrayFindSource,
  CookieSource,
  DataPointSource,
  GlobalJsSource,
  JsonLdSource,
  LocalStorageSource,
  MetaTagSource,
  PbjsEidSource,
} from '../types/collection';

/**
 * Per-collect() cache for expensive shared scans. JSON-LD script parsing and
 * array_find array resolution happen at most once per payload build, however
 * many points read from them.
 */
type CollectContext = {
  jsonLdNodes?: Record<string, unknown>[];
  arrays: Map<string, unknown[] | undefined>;
};

const createCollectContext = (): CollectContext => ({ arrays: new Map() });

const readGlobalJs = (source: GlobalJsSource): unknown => {
  if (typeof source.property_name !== 'string' || source.property_name.length === 0) {
    return undefined;
  }
  return resolvePath(window, source.property_name);
};

const readCookie = (source: CookieSource): unknown => {
  if (typeof source.property_name !== 'string' || source.property_name.length === 0) {
    return undefined;
  }
  const entries = document.cookie ? document.cookie.split('; ') : [];
  for (const entry of entries) {
    const separatorIndex = entry.indexOf('=');
    if (separatorIndex > -1 && entry.slice(0, separatorIndex) === source.property_name) {
      const rawValue = entry.slice(separatorIndex + 1);
      try {
        return decodeURIComponent(rawValue);
      } catch {
        return rawValue;
      }
    }
  }
  return undefined;
};

const readLocalStorage = (source: LocalStorageSource): unknown => {
  if (typeof source.property_name !== 'string' || source.property_name.length === 0) {
    return undefined;
  }
  // property_name is the literal storage key: dots in e.g. `UDR.DATA` are not a path.
  const rawValue = window.localStorage.getItem(source.property_name);
  if (rawValue === null) {
    return undefined;
  }
  if (!source.parse_json) {
    return rawValue;
  }
  const parsed = JSON.parse(rawValue);
  return source.json_path ? resolvePath(parsed, source.json_path) : parsed;
};

const readPbjsEid = (source: PbjsEidSource): unknown => {
  if (typeof source.eid_source !== 'string' || source.eid_source.length === 0) {
    return undefined;
  }
  return getPrebidUserId(source.eid_source);
};

const readAdserverKv = (source: AdserverKvSource): unknown => {
  if (typeof source.key !== 'string' || source.key.length === 0) {
    return undefined;
  }
  // Selection from the per-entry-point harvest (Page.harvestAdServerTargeting()).
  // Values are string[] for array KVs but plain string for scalar KVs.
  return GlobalFactStore.getInstance().getAdServerPageTargeting(source.key);
};

const readArrayFind = (source: ArrayFindSource, ctx: CollectContext): unknown => {
  if (
    typeof source.array_path !== 'string' ||
    typeof source.value_path !== 'string' ||
    typeof source.match?.path !== 'string'
  ) {
    return undefined;
  }
  if (!ctx.arrays.has(source.array_path)) {
    const resolved = resolvePath(window, source.array_path);
    ctx.arrays.set(source.array_path, Array.isArray(resolved) ? resolved : undefined);
  }
  const entries = ctx.arrays.get(source.array_path);
  if (!entries) {
    return undefined;
  }
  // Scan from the end: on SPAs the latest push wins.
  for (let i = entries.length - 1; i >= 0; i--) {
    const candidate = resolvePath(entries[i], source.match.path);
    if (candidate !== undefined && String(candidate) === source.match.equals) {
      return resolvePath(entries[i], source.value_path);
    }
  }
  return undefined;
};

const getJsonLdNodes = (ctx: CollectContext): Record<string, unknown>[] => {
  if (!ctx.jsonLdNodes) {
    const nodes: Record<string, unknown>[] = [];
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    for (const script of scripts) {
      let parsed: unknown;
      try {
        parsed = JSON.parse(script.textContent ?? '');
      } catch {
        continue; // Tolerate malformed JSON-LD blocks.
      }
      const candidates = Array.isArray(parsed) ? parsed : [parsed];
      for (const candidate of candidates) {
        if (candidate && typeof candidate === 'object') {
          nodes.push(candidate as Record<string, unknown>);
          const graph = (candidate as Record<string, unknown>)['@graph'];
          if (Array.isArray(graph)) {
            for (const graphNode of graph) {
              if (graphNode && typeof graphNode === 'object') {
                nodes.push(graphNode as Record<string, unknown>);
              }
            }
          }
        }
      }
    }
    ctx.jsonLdNodes = nodes;
  }
  return ctx.jsonLdNodes;
};

const readJsonLd = (source: JsonLdSource, ctx: CollectContext): unknown => {
  if (typeof source.schema_type !== 'string' || typeof source.value_path !== 'string') {
    return undefined;
  }
  for (const node of getJsonLdNodes(ctx)) {
    const nodeType = node['@type'];
    const matches = Array.isArray(nodeType)
      ? nodeType.includes(source.schema_type)
      : nodeType === source.schema_type;
    if (matches) {
      return resolvePath(node, source.value_path);
    }
  }
  return undefined;
};

const readMetaTag = (source: MetaTagSource): unknown => {
  if (
    (source.attribute !== 'property' && source.attribute !== 'name') ||
    typeof source.value !== 'string' ||
    source.value.length === 0
  ) {
    return undefined;
  }
  const escapedValue = source.value.replace(/(["\\])/g, '\\$1');
  return (
    document
      .querySelector(`meta[${source.attribute}="${escapedValue}"]`)
      ?.getAttribute('content') ?? undefined
  );
};

type Accessor = (source: any, ctx: CollectContext) => unknown;

const ACCESSORS: Record<DataPointSource['type'], Accessor> = {
  global_js: readGlobalJs,
  cookie: readCookie,
  local_storage: readLocalStorage,
  pbjs_eid: readPbjsEid,
  adserver_kv: readAdserverKv,
  array_find: readArrayFind,
  json_ld: readJsonLd,
  meta_tag: readMetaTag,
};

export { ACCESSORS, createCollectContext };
export type { Accessor, CollectContext };
